import { ArrowUpRight, Hammer, PencilRuler, Sparkles, Wrench } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useLenis, scrollToSection } from "@/components/LenisProvider";

const TALL_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_contact-connect-59/artifacts/r18sjx3j_IMG_1806.jpeg";

function Card({ icon: Icon, title, text, className = "", testId }) {
  return (
    <div
      data-testid={testId}
      className={`group rounded-3xl border border-cream-border bg-card p-9 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_rgba(13,21,38,0.1)] ${className}`}
    >
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-terracotta/30 text-terracotta transition-colors duration-300 group-hover:bg-terracotta group-hover:text-cream">
        <Icon className="h-5 w-5" strokeWidth={1.5} />
      </span>
      <h3 className="mt-6 font-serif text-3xl text-olive">{title}</h3>
      <p className="mt-3 leading-relaxed text-olive-mid">{text}</p>
    </div>
  );
}

export default function Services() {
  const lenis = useLenis();

  return (
    <section id="servicios" className="py-24 lg:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <p className="eyebrow">Nuestros servicios</p>
            <h2 className="mt-5 max-w-xl font-serif text-4xl leading-tight tracking-tight text-olive sm:text-5xl lg:text-6xl">
              Todo lo que tu cocina necesita, en un solo taller.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:max-w-sm">
            <p className="leading-relaxed text-olive-mid">
              Del primer boceto al último tirón de cajón: nos hacemos cargo de
              cada detalle para que tú solo disfrutes el resultado.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-12">
          <Reveal className="md:col-span-7 md:row-span-2" y={36}>
            <div
              data-testid="service-card-diseno"
              className="group relative h-full min-h-[460px] overflow-hidden rounded-3xl md:min-h-[580px]"
            >
              <img
                src={TALL_IMG}
                alt="Cocina gris y blanco instalada por Toscana"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-olive/90 via-olive/30 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-9 lg:p-11">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-cream/40 text-cream">
                  <PencilRuler className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <h3 className="mt-6 font-serif text-3xl text-cream lg:text-4xl">
                  Diseño Integral de Cocinas
                </h3>
                <p className="mt-3 max-w-md leading-relaxed text-cream/80">
                  Planos en 3D, selección de materiales y distribución ergonómica
                  pensada para cómo cocinas y vives cada día.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="md:col-span-5" y={36}>
            <Card
              icon={Hammer}
              title="Fabricación a Medida"
              text="Carpintería propia con madera, lambrín y herrajes de primera calidad. Cada pieza se corta para tu espacio."
              testId="service-card-fabricacion"
            />
          </Reveal>

          <Reveal delay={0.2} className="md:col-span-5" y={36}>
            <Card
              icon={Wrench}
              title="Instalación Premium"
              text="Equipo propio, fechas cumplidas y acabados impecables. Entregamos tu cocina lista para estrenarse."
              testId="service-card-instalacion"
            />
          </Reveal>

          <Reveal delay={0.1} className="md:col-span-7" y={36}>
            <div
              data-testid="service-card-remodelacion"
              className="group rounded-3xl border border-cream-border bg-card p-9 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_rgba(13,21,38,0.1)]"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-terracotta/30 text-terracotta transition-colors duration-300 group-hover:bg-terracotta group-hover:text-cream">
                <Sparkles className="h-5 w-5" strokeWidth={1.5} />
              </span>
              <h3 className="mt-6 font-serif text-3xl text-olive">
                Remodelación Completa
              </h3>
              <p className="mt-3 max-w-lg leading-relaxed text-olive-mid">
                ¿Tu cocina ya no te representa? Renovamos mobiliario, cubiertas,
                pisos y electricidad sin que dejes de usar tu casa.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="md:col-span-5" y={36}>
            <div
              data-testid="service-card-cta"
              className="flex h-full flex-col justify-between rounded-3xl bg-terracotta p-9 text-cream transition-colors duration-300 hover:bg-terracotta-dark"
            >
              <h3 className="font-serif text-3xl leading-snug">
                ¿Lista para empezar? La cita de diseño es sin costo.
              </h3>
              <a
                href="#contacto"
                data-testid="services-cta-button"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(lenis, "#contacto");
                }}
                className="mt-8 inline-flex items-center gap-2 text-sm font-medium uppercase tracking-[0.2em]"
              >
                Agenda tu visita
                <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
